import { useCallback, useState } from 'react';
import { Button, IconButton } from '@cbhq/cds-web/buttons';
import { SelectOption } from '@cbhq/cds-web/controls';
import { Dropdown } from '@cbhq/cds-web/dropdown';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { TextDisplay3 } from '@cbhq/cds-web/typography';

import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { CHART_DATA } from ':cloud/widgets/earnings/config';
import { ProtocolRewardsChart } from ':cloud/widgets/earnings/ProtocolRewardsChart';
import {
  EarningsChartSelection,
  EarningsRewardsChartProps,
  ProtocolData,
} from ':cloud/widgets/earnings/types';
import { LoadingSpinner } from ':cloud/widgets/sharedcomponents';
import { EmptyChart } from ':cloud/widgets/sharedcomponents/EmptyChart';

const DEFAULT_PROTOCOL = 'ethereum';

const content = Object.entries(CHART_DATA).map(([key, title]) => (
  <SelectOption borderRadius="rounded" key={key} value={key} title={title} />
));

export function EarningsRewardsChart({
  isLoading,
  chartType,
  protocolData,
  totalGrossRewards,
  totalNetRewards,
  latestStakeBalance,
}: EarningsRewardsChartProps) {
  const { isPhone } = useSimpleBreakpoints();
  const [chartSelection, setChartSelection] = useState<EarningsChartSelection>('totalGrossRewards');
  const handleSwitchChartData = useCallback(
    (value: EarningsChartSelection) => {
      setChartSelection(value);
    },
    [setChartSelection],
  );

  if (isLoading) return <LoadingSpinner />;

  if (!isLoading && isProtocolDataEmpty(protocolData)) {
    return <EmptyChart chartType={chartType} />;
  }

  let totalValue: string;
  switch (chartSelection) {
    case 'totalGrossRewards':
      totalValue = totalGrossRewards;
      break;
    case 'totalNetRewards':
      totalValue = totalNetRewards;
      break;
    case 'stakedBalance':
      totalValue = latestStakeBalance;
      break;
    default:
      totalValue = '0';
      break;
  }

  return (
    <>
      <HStack gap={0.5} spacingBottom={5} justifyContent="space-between">
        <Dropdown
          value="1"
          onChange={(value) => handleSwitchChartData(value)}
          content={<VStack width={208}>{content}</VStack>}
          enableMobileModal
        >
          {isPhone ? (
            <IconButton name="more" />
          ) : (
            <Button compact endIcon="caretDown" variant="secondary">
              {CHART_DATA[chartSelection]}
            </Button>
          )}
        </Dropdown>
        {totalValue && (
          <TextDisplay3 as="h2">{`${totalValue} ${protocolData.ticker}`}</TextDisplay3>
        )}
      </HStack>
      <ProtocolRewardsChart
        protocol={DEFAULT_PROTOCOL}
        ticker={protocolData.ticker}
        chartSelection={chartSelection}
        protocolData={protocolData}
      />
    </>
  );
}

function isProtocolDataEmpty(protocolData: ProtocolData) {
  return (
    Object.keys(protocolData.rewards).length === 0 ||
    Object.keys(protocolData.balances).length === 0
  );
}
